import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Pixel art mascot - a friendly data robot named "Chippy"
const ChippyMascot = ({ mood = 'happy' }) => (
  <svg viewBox="0 0 48 48" className="w-full h-full" style={{ imageRendering: 'pixelated' }}>
    {/* Body */}
    <rect x="12" y="20" width="24" height="20" fill="#4169E1" />
    <rect x="14" y="22" width="20" height="16" fill="#6495ED" />

    {/* Head */}
    <rect x="10" y="4" width="28" height="18" fill="#4169E1" />
    <rect x="12" y="6" width="24" height="14" fill="#6495ED" />

    {/* Antenna */}
    <rect x="22" y="0" width="4" height="6" fill="#FFD700" />
    <rect x="20" y="0" width="8" height="2" fill="#FF6347" />

    {/* Eyes */}
    {mood === 'happy' ? (
      <>
        <rect x="16" y="10" width="4" height="4" fill="#00FF00" />
        <rect x="28" y="10" width="4" height="4" fill="#00FF00" />
        {/* Happy eye shine */}
        <rect x="17" y="11" width="2" height="2" fill="#90EE90" />
        <rect x="29" y="11" width="2" height="2" fill="#90EE90" />
      </>
    ) : mood === 'wink' ? (
      <>
        <rect x="16" y="10" width="4" height="4" fill="#00FF00" />
        <rect x="28" y="12" width="4" height="2" fill="#00FF00" />
      </>
    ) : (
      <>
        <rect x="16" y="10" width="4" height="4" fill="#FF69B4" />
        <rect x="28" y="10" width="4" height="4" fill="#FF69B4" />
      </>
    )}

    {/* Mouth */}
    <rect x="20" y="16" width="8" height="2" fill="#1a0a2e" />
    {mood === 'happy' && (
      <>
        <rect x="18" y="14" width="2" height="2" fill="#1a0a2e" />
        <rect x="28" y="14" width="2" height="2" fill="#1a0a2e" />
      </>
    )}

    {/* Screen/chest display */}
    <rect x="18" y="26" width="12" height="8" fill="#1a0a2e" />
    <rect x="20" y="28" width="2" height="2" fill="#00FF00" />
    <rect x="24" y="28" width="2" height="2" fill="#FFD700" />
    <rect x="20" y="32" width="8" height="1" fill="#FF0080" />

    {/* Arms */}
    <rect x="6" y="22" width="6" height="4" fill="#4169E1" />
    <rect x="36" y="22" width="6" height="4" fill="#4169E1" />
    <rect x="4" y="24" width="4" height="6" fill="#6495ED" />
    <rect x="40" y="24" width="4" height="6" fill="#6495ED" />

    {/* Legs */}
    <rect x="16" y="40" width="6" height="6" fill="#4169E1" />
    <rect x="26" y="40" width="6" height="6" fill="#4169E1" />
    <rect x="14" y="44" width="8" height="4" fill="#FFD700" />
    <rect x="26" y="44" width="8" height="4" fill="#FFD700" />
  </svg>
);

const mascotMessages = [
  { text: "Ready to earn some coins?", mood: "happy" },
  { text: "Complete quests for rewards!", mood: "wink" },
  { text: "Your data = Your cash!", mood: "happy" },
  { text: "Join the leaderboard!", mood: "excited" },
  { text: "Let's get questing!", mood: "happy" },
  { text: "Nice to meet you!", mood: "wink" },
];

const ArcadeMascot = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // Show mascot after a delay
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, 3000);

    // Cycle through messages
    const messageInterval = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % mascotMessages.length);
    }, 8000);

    return () => {
      clearTimeout(showTimer);
      clearInterval(messageInterval);
    };
  }, []);

  // Handle dragging
  const handleDrag = (event, info) => {
    setPosition({ x: info.point.x, y: info.point.y });
  };

  if (!isVisible) return null;

  return (
    <motion.div
      className="fixed z-[60]"
      initial={{ bottom: -100, right: 80 }}
      animate={{ bottom: 100, right: 80 }}
      transition={{ type: 'spring', stiffness: 100, damping: 15 }}
      style={{ cursor: 'none' }}
    >
      {/* Speech bubble */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0, y: 10 }}
            className="absolute bottom-full right-0 mb-2 bg-panel-purple border-2 border-neon-pink rounded-lg px-4 py-2 min-w-[160px]"
            style={{ boxShadow: '0 0 20px rgba(255,0,128,0.3)' }}
          >
            <p className="font-pixel text-[8px] text-foreground whitespace-nowrap">
              {mascotMessages[currentMessage].text}
            </p>
            {/* Speech bubble arrow */}
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-panel-purple border-r-2 border-b-2 border-neon-pink transform rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mascot container */}
      <motion.div
        drag
        dragConstraints={{ left: -300, right: 0, top: -400, bottom: 0 }}
        dragElastic={0.1}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsExpanded(!isExpanded)}
        onDrag={handleDrag}
        className="relative"
      >
        {/* Glow effect */}
        <motion.div
          className="absolute inset-0 bg-neon-pink/30 blur-xl rounded-full"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />

        {/* Mascot */}
        <motion.div
          className="w-16 h-16 relative z-10"
          animate={{
            y: [0, -8, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <ChippyMascot mood={mascotMessages[currentMessage].mood} />
        </motion.div>

        {/* Interaction hint */}
        <motion.div
          className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <span className="font-pixel text-[6px] text-lavender">
            {isExpanded ? 'drag me!' : 'click me!'}
          </span>
        </motion.div>

        {/* Pixel sparkles around mascot */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-trophy-gold"
            style={{
              left: `${20 + i * 25}%`,
              top: `${10 + i * 15}%`,
            }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: i * 0.5,
            }}
          />
        ))}
      </motion.div>
    </motion.div>
  );
};

export default ArcadeMascot;
